import apiClient from '../client';

// Types
export interface User {
  id: number;
  user_id: string;
  username: string;
  email: string;
  full_name?: string;
  is_active: boolean;
  is_superuser: boolean;
  company_id?: number;
  employee_id?: number;
  phone?: string;
  avatar_url?: string;
  last_login?: string;
  created_at: string;
  roles: Role[];
}

export interface Role {
  id: number;
  role_id: string;
  name: string;
  display_name: string;
  description?: string;
  is_system_role: boolean;
  permissions: Permission[];
}

export interface Permission {
  id: number;
  name: string;
  display_name: string;
  description?: string;
  category: string;
}

export interface UserSession {
  id: number;
  session_id: string;
  user_id: string;
  ip_address?: string;
  user_agent?: string;
  created_at: string;
  expires_at: string;
  last_activity: string;
  is_active: boolean;
}

// Login/Auth Types
export interface LoginRequest {
  username: string;
  password: string;
}

export interface LoginResponse {
  session_token: string;
  expires_at: string;
  user: {
    id: number;
    username: string;
    email: string;
    full_name: string;
    roles: string[];
    is_admin: boolean;
  };
}

// API Functions
export const usersService = {
  // Login
  login: async (credentials: LoginRequest): Promise<LoginResponse> => {
    const response = await apiClient.post('/users/login', credentials);
    const data = response.data;

    // Store session token in localStorage
    if (data.session_token) {
      localStorage.setItem('auth_token', data.session_token);
      localStorage.setItem('user', JSON.stringify(data.user));
    }

    return data;
  },

  // Logout
  logout: async (): Promise<void> => {
    try {
      await apiClient.post('/users/logout');
    } finally {
      // Always clear local storage
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
    }
  },

  // Get current user
  getCurrentUser: async (): Promise<{ success: boolean; user: User }> => {
    const response = await apiClient.get('/users/me');
    return response.data;
  },

  // Update current user
  updateCurrentUser: async (data: Partial<User>): Promise<{ success: boolean; user: User }> => {
    const response = await apiClient.put('/users/me', data);
    return response.data;
  },

  // Change password
  changePassword: async (data: {
    current_password: string;
    new_password: string;
  }): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.post('/users/me/change-password', data);
    return response.data;
  },

  // Get all users (admin)
  getUsers: async (params?: {
    is_active?: boolean;
    role_id?: string;
    company_id?: number;
    search?: string;
    skip?: number;
    limit?: number;
  }): Promise<{ users: User[]; total: number }> => {
    const response = await apiClient.get('/users/users', { params });
    // Backend returns a direct list of users
    if (Array.isArray(response.data)) {
      return { users: response.data, total: response.data.length };
    }
    return response.data;
  },

  // Get user by ID (admin)
  getUser: async (userId: string): Promise<{ success: boolean; user: User }> => {
    const response = await apiClient.get(`/users/users/${userId}`);
    return response.data;
  },

  // Create user (admin)
  createUser: async (data: {
    username: string;
    email: string;
    password: string;
    full_name?: string;
    company_id?: number;
    employee_id?: number;
    role_ids?: string[];
  }): Promise<{ success: boolean; user: User }> => {
    const response = await apiClient.post('/users/users', data);
    return response.data;
  },

  // Update user (admin)
  updateUser: async (userId: string, data: Partial<User>): Promise<{ success: boolean; user: User }> => {
    const response = await apiClient.put(`/users/users/${userId}`, data);
    return response.data;
  },

  // Delete user (admin)
  deleteUser: async (userId: string): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.delete(`/users/users/${userId}`);
    return response.data;
  },

  // Activate/deactivate user (admin)
  toggleUserStatus: async (userId: string, isActive: boolean): Promise<{ success: boolean; user: User }> => {
    const response = await apiClient.post(`/users/users/${userId}/toggle-status`, { is_active: isActive });
    return response.data;
  },

  // Reset user password (admin)
  resetUserPassword: async (userId: string, newPassword: string): Promise<{
    success: boolean;
    message: string;
  }> => {
    const response = await apiClient.post(`/users/users/${userId}/reset-password`, { new_password: newPassword });
    return response.data;
  },

  // Roles - NOTE: Backend doesn't have a general /roles endpoint yet
  // This returns empty data until the endpoint is implemented
  getRoles: async (params?: {
    skip?: number;
    limit?: number;
  }): Promise<{ roles: Role[]; total: number }> => {
    // TODO: Backend needs to implement GET /users/roles endpoint
    // For now, return empty data
    return { roles: [], total: 0 };
    // const response = await apiClient.get('/users/roles', { params });
    // return response.data;
  },

  // Get role by ID
  getRole: async (roleId: string): Promise<{ success: boolean; role: Role }> => {
    const response = await apiClient.get(`/users/roles/${roleId}`);
    return response.data;
  },

  // Create role (admin)
  createRole: async (data: {
    name: string;
    display_name: string;
    description?: string;
    permission_ids?: number[];
  }): Promise<{ success: boolean; role: Role }> => {
    const response = await apiClient.post('/users/roles', data);
    return response.data;
  },

  // Update role (admin)
  updateRole: async (roleId: string, data: Partial<Role>): Promise<{ success: boolean; role: Role }> => {
    const response = await apiClient.put(`/users/roles/${roleId}`, data);
    return response.data;
  },

  // Delete role (admin)
  deleteRole: async (roleId: string): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.delete(`/users/roles/${roleId}`);
    return response.data;
  },

  // Assign role to user
  assignRole: async (userId: string, roleId: string): Promise<{ success: boolean; user: User }> => {
    const response = await apiClient.post(`/users/users/${userId}/roles/${roleId}`);
    return response.data;
  },

  // Remove role from user
  removeRole: async (userId: string, roleId: string): Promise<{ success: boolean; user: User }> => {
    const response = await apiClient.delete(`/users/users/${userId}/roles/${roleId}`);
    return response.data;
  },

  // Permissions
  getPermissions: async (params?: {
    category?: string;
    skip?: number;
    limit?: number;
  }): Promise<{ permissions: Permission[]; total: number }> => {
    const response = await apiClient.get('/users/permissions', { params });
    return response.data;
  },

  // Get user permissions
  getUserPermissions: async (userId: string): Promise<{ success: boolean; permissions: Permission[] }> => {
    const response = await apiClient.get(`/users/users/${userId}/permissions`);
    return response.data;
  },

  // Sessions
  getSessions: async (params?: {
    user_id?: string;
    is_active?: boolean;
    skip?: number;
    limit?: number;
  }): Promise<{ sessions: UserSession[]; total: number }> => {
    // TODO: Backend needs to implement GET /users/sessions endpoint
    // For now, return empty data
    return { sessions: [], total: 0 };
    // const response = await apiClient.get('/users/sessions', { params });
    // return response.data;
  },

  getUserSessions: async (userId: string): Promise<{ success: boolean; sessions: UserSession[] }> => {
    const response = await apiClient.get(`/users/users/${userId}/sessions`);
    return response.data;
  },

  // Revoke session
  revokeSession: async (sessionId: string): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.delete(`/users/sessions/${sessionId}`);
    return response.data;
  },

  // Revoke all sessions for user
  revokeAllSessions: async (userId: string): Promise<{ success: boolean; revoked_count: number }> => {
    const response = await apiClient.post(`/users/users/${userId}/revoke-all-sessions`);
    return response.data;
  },

  // Get user activity
  getUserActivity: async (userId: string, days: number = 30): Promise<{
    success: boolean;
    activity: {
      total_logins: number;
      last_login: string;
      actions_performed: number;
      by_action_type: { [key: string]: number };
      recent_actions: any[];
    };
  }> => {
    const response = await apiClient.get(`/users/users/${userId}/activity?days=${days}`);
    return response.data;
  },
};

export default usersService;

